import { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Box } from '@mui/material';
import { toast } from 'sonner';
import ConfirmDialog from 'Components/ConfirmDialog';
import useTranslation from 'Hooks/useTranslation';
import SessionsKpis from './Components/SessionsKpis';
import SessionsTable from './Components/SessionsTable';
import SessionFormDialog from './Components/SessionFormDialog';
import SessionDetailDialog from './Components/SessionDetailDialog';
import useSessionsListApi from './Hooks/useSessionsListApi';
import useSessionsStatsApi from './Hooks/useSessionsStatsApi';
import useSessionDetailApi from './Hooks/useSessionDetailApi';

const SEARCH_DEBOUNCE_MS = 350;

/**
 * Page composing the in-course "Sesiones" tab.
 *
 * Responsibilities (SRP):
 *   - Owns the UI state (search, statusFilter, which dialogs are open).
 *   - Orchestrates the data hooks; never knows about axios or backend shape.
 *   - Delegates per-row interactions to the table / detail dialog.
 *
 * Children (KPIs / Table / Dialogs) are pure presentational — they receive
 * data and emit events. Any state mutation routed through this page makes
 * coordinated refetches simple.
 */
export default function EditCourseSessions({ courseId }) {
  const { t2 } = useTranslation('EditCourseSessions');

  // Toolbar state
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [viewMode, setViewMode] = useState('list');

  // Dialog state
  const [formOpen, setFormOpen] = useState(false);
  const [formMode, setFormMode] = useState('create');
  const [editTarget, setEditTarget] = useState(null);

  const [detailOpen, setDetailOpen] = useState(false);
  const [detailSessionId, setDetailSessionId] = useState(null);

  const [submitting, setSubmitting] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);

  const { data: sessions, loading, fetchSessions } = useSessionsListApi({ courseId, init: false });
  const { stats, loading: loadingStats, fetchStats } = useSessionsStatsApi({ courseId, init: false });
  const { create, update, softDelete } = useSessionDetailApi({ sessionId: null, init: false });

  // Debounce the search input → debouncedSearch (the value actually sent to the API).
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [search]);

  // Re-fetch list whenever course, search, or status filter change.
  useEffect(() => {
    if (!courseId) return;
    fetchSessions({ search: debouncedSearch, status: statusFilter });
  }, [courseId, debouncedSearch, statusFilter, fetchSessions]);

  useEffect(() => {
    if (!courseId) return;
    fetchStats();
  }, [courseId, fetchStats]);

  const refreshAll = useCallback(async () => {
    // Run both refetches in parallel and resolve only when both have updated
    // their state. Callers `await` this so the UI does not flash stale data.
    await Promise.all([fetchSessions({ search: debouncedSearch, status: statusFilter }), fetchStats()]);
  }, [fetchSessions, fetchStats, debouncedSearch, statusFilter]);

  // ── Handlers ────────────────────────────────────────────────────────────

  const handleOpenCreate = () => {
    setFormMode('create');
    setEditTarget(null);
    setFormOpen(true);
  };

  const handleOpenEdit = session => {
    setFormMode('edit');
    setEditTarget(session);
    setFormOpen(true);
  };

  const handleSubmitSession = async payload => {
    setSubmitting(true);
    const response = formMode === 'create' ? await create(payload) : await update(editTarget.id, payload);
    if (!response.error) {
      toast.success(t2(formMode === 'create' ? 'toasts.sessionCreated' : 'toasts.sessionUpdated'));
      // Wait for the refetch to complete BEFORE returning so the dialog only
      // closes once the table has the new row.
      await refreshAll();
    }
    setSubmitting(false);
    return response;
  };

  // Opens the confirm dialog; the actual soft-delete happens in confirmDelete.
  const handleDelete = session => setPendingDelete(session);

  const confirmDelete = async () => {
    const session = pendingDelete;
    setPendingDelete(null);
    if (!session) return;
    const response = await softDelete(session.id);
    if (response.error) {
      toast.error(response.error);
      return;
    }
    toast.success(t2('toasts.sessionDeleted'));
    if (detailOpen && detailSessionId === session.id) {
      setDetailOpen(false);
      setDetailSessionId(null);
    }
    await refreshAll();
  };

  const handleOpenDetail = session => {
    setDetailSessionId(session.id);
    setDetailOpen(true);
  };

  const handleEditFromDetail = session => {
    setDetailOpen(false);
    handleOpenEdit(session);
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, pb: 8 }}>
      <SessionsKpis stats={stats} loading={loadingStats} />

      <SessionsTable
        sessions={sessions}
        loading={loading}
        search={search}
        statusFilter={statusFilter}
        viewMode={viewMode}
        onSearchChange={setSearch}
        onStatusFilterChange={setStatusFilter}
        onViewModeChange={setViewMode}
        onCreate={handleOpenCreate}
        onOpenDetail={handleOpenDetail}
        onEdit={handleOpenEdit}
        onDelete={handleDelete}
      />

      <SessionFormDialog
        open={formOpen}
        mode={formMode}
        courseId={courseId}
        initialSession={editTarget}
        onClose={() => setFormOpen(false)}
        onSubmitSession={handleSubmitSession}
        submitting={submitting}
      />

      <SessionDetailDialog
        open={detailOpen}
        sessionId={detailSessionId}
        onClose={() => setDetailOpen(false)}
        onChanged={refreshAll}
        onEdit={handleEditFromDetail}
        onDelete={handleDelete}
      />

      <ConfirmDialog
        open={!!pendingDelete}
        title={t2('deleteSessionTitle')}
        description={t2('deleteSessionMessage')}
        confirmText={t2('detail.delete')}
        cancelText={t2('form.cancel')}
        onConfirm={confirmDelete}
        onCancel={() => setPendingDelete(null)}
      />
    </Box>
  );
}

EditCourseSessions.propTypes = {
  courseId: PropTypes.string,
};

EditCourseSessions.defaultProps = { courseId: null };
